import React, { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import {
  showReduxModal,
  hideReduxModal,
  resetReduxModal,
} from 'Reducers/UI/actions'

import Loading from '../../../../../Components/Campaigns/Page/Conversions/Loading'
import UploadSuccess from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/UploadSuccess'
import ValidationError from '../../../../../Components/Campaigns/Page/Conversions/ModalBody/ValidationError'
import ConversionsModalTable from '../../../../../Components/Campaigns/Page/Conversions/ConversionsModalTable'

import {
  SHOW_ERROR_NUMBER,
  WRONG_FILE_STRUCTURE,
  MISSING_VALUE,
  NOT_EXIST_METRIC,
  DUPLICATE_FLYER_CODE,
  NOT_EXIST_FLYER_CODE,
  NOT_COMPLETED_MISSION_FLYER_CODE,
  WRONG_CONVERSION_ORDER,
  LESS_CONVERSIONS_THAN_DB,
  INVALID_TIMESTAMP,
  NO_NEW_CONVERSION,
  invalidFileStructureTableContent,
  errorReportCSVFileContent,
  lessConversionsCSVFileContent,
  missingValuesTableContent,
  nonExistMetricsTableContent,
  wrongFlyerCodesTableContent,
  wrongConversionOrderTableContent,
  lessConversionsTableContent,
  invalidTimestampTableContent,
} from '../../../../../Utils/Conversion'

const useConversionsUpload = withTimestamp => {
  const dispatch = useDispatch()

  const hideModal = useCallback(() => {
    dispatch(hideReduxModal())
    setTimeout(() => {
      dispatch(resetReduxModal())
    }, 150)
  }, [])

  const startRunningValidation = () => {
    dispatch(
      showReduxModal({
        title: 'Report validation in process...',
        children: <Loading />,
        isReportModal: false,
        hideButtons: true,
        aspect: 'conversions',
      }),
    )
  }

  const startRunningDeleting = () => {
    dispatch(
      showReduxModal({
        title: 'Report deleting in process...',
        children: <Loading />,
        isReportModal: false,
        hideButtons: true,
        aspect: 'conversions',
      }),
    )
  }

  const handleRefresh = () => {
    window.location.reload(true)
  }

  const handleConversionsUploadSuccess = () => {
    dispatch(
      showReduxModal({
        title: '',
        onConfirm: handleRefresh,
        children: <UploadSuccess />,
        isReportModal: false,
        hideCancel: true,
        confirmLabel: 'OK',
        aspect: 'conversions',
      }),
    )
  }

  const handleShowErrorModal = ({
    title = 'Title',
    content = 'Content',
    info = null,
    headers = [],
    rows = [],
    templateCSVFile = null,
    hideButtons = true,
    confirmLabel = null,
    cancelLabel = null,
    hideCancel = true,
    onConfirm1,
    onConfirm2,
    onCancel,
    buttonLabel1,
    buttonLabel2,
    buttonLabel3,
    showTimestampTemplate = false,
  }) => {
    dispatch(
      showReduxModal({
        title: '',
        children: (
          <ValidationError
            title={title}
            content={content}
            showTimestampTemplate={showTimestampTemplate}
            info={info}
          >
            <ConversionsModalTable
              headers={headers}
              rows={rows}
              templateCSVFile={templateCSVFile}
              onConfirm1={onConfirm1}
              onConfirm2={onConfirm2}
              onCancel={onCancel}
              buttonLabel1={buttonLabel1}
              buttonLabel2={buttonLabel2}
              buttonLabel3={buttonLabel3}
            />
          </ValidationError>
        ),
        hideButtons,
        isReportModal: true,
        hideCancel: hideCancel,
        confirmLabel: confirmLabel ? confirmLabel : 'OK',
        cancelLabel: cancelLabel ? cancelLabel : 'Cancel',
        aspect: 'conversions',
      }),
    )
  }

  const handleWrongFileStructure = ({ metrics, fileHeader, fileData }) => {
    const title = 'Bad headers'
    const content = 'Some headers in the file are wrong.'
    const { headers, rows } = invalidFileStructureTableContent(withTimestamp, {
      metrics,
      fileHeader,
      fileData,
    })

    handleShowErrorModal({
      title,
      content,
      headers,
      rows,
      hideButtons: false,
      confirmLabel: 'Cancel',
    })
  }

  const handleMissingValues = ({
    missingValues = [],
    csvHeaders,
    tableData,
  }) => {
    const title = 'File contains empty values'
    const content =
      missingValues.length > SHOW_ERROR_NUMBER
        ? 'The file contains too many errors to display here. Please download the error log file.'
        : null
    const info = `${missingValues.length} errors found`
    const { headers, rows } = missingValuesTableContent({
      missingValues,
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({
      missingValues,
      csvHeaders,
      tableData,
    })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
    })
  }

  const handleNonExistMetrics = ({ csvHeaders, tableData }) => {
    const title = 'Incorrect metric names'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? 'Too many incorrect metric names to display all of them.'
        : 'The following metric names do not exist in this campaign'
    const info = `${tableData.length} errors found`
    const { headers, rows } = nonExistMetricsTableContent({
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({ csvHeaders, tableData })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
    })
  }

  const handleDuplicatedFlyerCodes = (
    handleConfirm,
    handleCancel,
    { csvHeaders, tableData },
  ) => {
    const title = 'Duplicate codes in file'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? 'The file contains too many errors to display here. Please download the error log file.'
        : 'Some flyer codes appear more than once in the file.'
    const info = `${tableData.length} errors found`
    const { headers, rows } = wrongFlyerCodesTableContent({
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({ csvHeaders, tableData })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
      onConfirm1: handleConfirm,
      onCancel: handleCancel,
      buttonLabel1: 'Upload codes with highest nr of conversions',
      buttonLabel3: 'Cancel',
    })
  }

  const handleIncorrectFlyerCodes = (
    handleConfirm,
    handleCancel,
    { csvHeaders, tableData },
    withTimestamp,
  ) => {
    const title = 'Unknown flyer codes'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? `Too many incorrect flyer codes to display all of them.\n${
            withTimestamp
              ? 'Please review the codes and try again.'
              : 'Click “Upload only relevant codes” if you want these unknown flyer codes to be ignored.'
          }`
        : `The following flyer codes do not exist in this campaign.\n${
            withTimestamp
              ? 'Please review the codes and try again.'
              : 'Click “Upload only relevant codes” if you want these unknown flyer codes to be ignored.'
          }`
    const info = `${tableData.length} errors found`
    const { headers, rows } = wrongFlyerCodesTableContent({
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({ csvHeaders, tableData })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
      onConfirm1: withTimestamp ? null : handleConfirm,
      onCancel: withTimestamp ? null : handleCancel,
      buttonLabel1: withTimestamp ? null : 'Upload only relevant codes',
      buttonLabel3: withTimestamp ? null : 'Cancel',
    })
  }

  const handleNotCompletedMissionFlyerCodes = (
    handleConfirm,
    handleCancel,
    { csvHeaders, tableData },
  ) => {
    const title = 'Incomplete missions found'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? 'Too many flyer codes which are not assigned to any mission or assigned to incomplete missions.'
        : 'The file contains conversions for flyer codes that are not assigned to any mission or assigned to incomplete missions.'
    const info = `${tableData.length} errors found`
    const { headers, rows } = wrongFlyerCodesTableContent({
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({ csvHeaders, tableData })

    const handleConfirm1 = () => {
      handleConfirm(0)
    }
    const handleConfirm2 = () => {
      handleConfirm(1)
    }
    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
      onConfirm1: handleConfirm1,
      onConfirm2: handleConfirm2,
      onCancel: handleCancel,
      buttonLabel1: 'Upload everything',
      buttonLabel2: 'Upload, but ignore incomplete',
      buttonLabel3: 'Cancel',
    })
  }

  const handleWrongConversionOrder = ({
    csvHeaders,
    tableData,
    compareOrderResult,
  }) => {
    const title = 'The file order appears to have changed'
    const content =
      'We’ve checked a few random rows and there is a mismatch between this file and what was previously uploaded.'
    const { headers, rows } = wrongConversionOrderTableContent({
      csvHeaders,
      tableData,
      compareOrderResult,
    })

    handleShowErrorModal({
      title,
      content,
      headers,
      rows,
      hideButtons: false,
    })
  }

  const handleLessConversions = (
    handleConfirm,
    handleCancel,
    { metrics, tableData },
    withTimestamp,
  ) => {
    const title = 'Fewer conversions'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? 'The file contains too many errors to display here. Please download the error log file.'
        : 'Some flyer codes in the file have less conversions than were uploaded previously.\nThis should not be possible.\n \n If you decide to upload anyway, we will keep the higher number of conversions for these codes.\n We will not upload the lower number.'
    const info = `${tableData.length} errors found`
    const { headers, rows } = lessConversionsTableContent({
      metrics,
      tableData,
    })
    const templateCSVFile = lessConversionsCSVFileContent({
      metrics,
      tableData,
    })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
      onConfirm1: withTimestamp ? null : handleConfirm,
      onCancel: withTimestamp ? null : handleCancel,
      buttonLabel1: withTimestamp ? null : 'Upload',
      buttonLabel3: withTimestamp ? null : 'Cancel',
    })
  }

  const handleInvalidTimestamp = ({ csvHeaders, tableData }) => {
    const title = 'Incorrect timestamps'
    const content =
      tableData.length > SHOW_ERROR_NUMBER
        ? 'The file contains too many errors to display here. Please download the error log file.'
        : 'Some timestamps appear to be inaccurate. Timestamps should be as follows:'
    const info = `${tableData.length} errors found`
    const { headers, rows } = invalidTimestampTableContent({
      csvHeaders,
      tableData,
    })
    const templateCSVFile = errorReportCSVFileContent({ csvHeaders, tableData })

    handleShowErrorModal({
      title,
      content,
      info,
      headers,
      rows,
      templateCSVFile,
      showTimestampTemplate: true,
    })
  }

  const handleNoNewConversion = () => {
    const title = 'File contains no new conversions.'
    const content =
      'This file does not appear to contain any new conversions. All conversions in this file are already saved in db.'

    handleShowErrorModal({
      title,
      content,
      hideButtons: false,
      confirmLabel: 'Close',
    })
  }

  const handleConversionsUploadError = (
    errorType,
    handleConfirm,
    handleCancel,
    data,
    withTimestamp,
  ) => {
    switch (errorType) {
      case WRONG_FILE_STRUCTURE:
        handleWrongFileStructure(data)
        break
      case MISSING_VALUE:
        handleMissingValues(data)
        break
      case NOT_EXIST_METRIC:
        handleNonExistMetrics(data)
        break
      case DUPLICATE_FLYER_CODE:
        handleDuplicatedFlyerCodes(handleConfirm, handleCancel, data)
        break
      case NOT_EXIST_FLYER_CODE:
        handleIncorrectFlyerCodes(
          handleConfirm,
          handleCancel,
          data,
          withTimestamp,
        )
        break
      case NOT_COMPLETED_MISSION_FLYER_CODE:
        handleNotCompletedMissionFlyerCodes(handleConfirm, handleCancel, data)
        break
      case WRONG_CONVERSION_ORDER:
        handleWrongConversionOrder(data)
        break
      case LESS_CONVERSIONS_THAN_DB:
        handleLessConversions(handleConfirm, handleCancel, data, withTimestamp)
        break
      case INVALID_TIMESTAMP:
        handleInvalidTimestamp(data)
        break
      case NO_NEW_CONVERSION:
        handleNoNewConversion()
        break
      default:
        break
    }
  }

  return {
    hideModal,
    handleRefresh,
    startRunningValidation,
    startRunningDeleting,
    handleConversionsUploadSuccess,
    handleConversionsUploadError,
  }
}

export default useConversionsUpload
